import React, { useState } from 'react'
import { Box, Skeleton } from '@mui/material'
import { EmtIcon, EmtPaper, EmtTypography } from 'emt-components-v2'
import { formatDate, formatTime } from '@/utils/formatDate'
import { useTranslation } from 'next-i18next'
import type { SxProps, Theme } from '@mui/material'

interface DateInterval {
  start: string
  end: string
  productSlug: string
}

interface TransformedDate {
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  productSlug: string
}

interface ReservationDatesProps {
  dates: DateInterval[]
  icon?: string
  showAll?: boolean
  borderColor?: string
  actionText?: () => void
  isLoading?: boolean // add loading prop
  variant?: 'default' | 'showService'
  sx?: SxProps<Theme>
}

const TITLES_MAP = {
  petsitter_home_sitting: { title: 'Garde chez le pet sitter', icon: 'emt-sit-home' },
  customer_home_sitting: { title: 'Garde chez le propriétaire', icon: 'emt-sit' },
  walk: { title: 'Promenade(s)', icon: 'emt-walk' },
  visit: { title: 'Visite(s) à domicile', icon: 'emt-visit' },
  walk_hour: { title: 'Promenade(s)', icon: 'emt-walk' },
  visit_hour: { title: 'Visite(s) à domicile', icon: 'emt-visit' },
  walk_half_hour: { title: 'Promenade(s)', icon: 'emt-walk' },
  visit_half_hour: { title: 'Visite(s) à domicile', icon: 'emt-visit' },
}

export const ReservationDates: React.FC<ReservationDatesProps> = ({
  dates,
  icon = 'emt-appointments',
  showAll = false,
  borderColor = '#F0F0F2',
  actionText,
  isLoading, // add loading prop
  variant = 'default',
  sx,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const { t: tc } = useTranslation('common')

  if (isLoading) {
    return (
      <Skeleton
        variant="rectangular"
        animation="wave"
        sx={{
          width: '100%',
          height: 70,
          borderRadius: '16px',
          padding: '14px 16px',
        }}
      />
    )
  }

  if (!dates || dates.length === 0) return null

  function transformDates(datesArray: DateInterval[]): TransformedDate[] {
    return datesArray.map(({ start, end, productSlug }) => {
      const [startDate, startTimeRaw] = start.split('T')
      const [endDate, endTimeRaw] = end.split('T')
      return {
        startDate,
        startTime: formatTime(startTimeRaw),
        endDate,
        endTime: formatTime(endTimeRaw),
        productSlug,
      }
    })
  }

  const transformedDates: TransformedDate[] = transformDates(dates)

  // Variant: if showAll prop is true, directly render all dates.
  if (showAll) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', ...sx }}>
        {transformedDates.map((date, index) => {
          let title, subtitle, iconName
          const defaultTitle =
            date.startDate === date.endDate
              ? `Le ${formatDate(date.startDate, 'dd/MM/yy')}`
              : `Du ${formatDate(date.startDate, 'dd/MM/yy')} au ${formatDate(date.endDate, 'dd/MM/yy')}`

          let defaultSubtitle = date.startTime
          if (date.productSlug === 'petsitter_home_sitting' || date.productSlug === 'customer_home_sitting') {
            defaultSubtitle = `Arrivée : ${date.startTime}, Départ : ${date.endTime}`
          } else if (date.productSlug === 'visit_hour' || date.productSlug === 'walk_hour') {
            defaultSubtitle = `${date.startTime} (60 minutes)`
          } else if (date.productSlug === 'visit_half_hour' || date.productSlug === 'walk_half_hour') {
            defaultSubtitle = `${date.startTime} (30 minutes)`
          }

          if (variant === 'showService') {
            title = TITLES_MAP[date.productSlug]?.title || date.productSlug
            subtitle = defaultTitle
            iconName = TITLES_MAP[date.productSlug]?.icon || 'emt-appointments'
          } else {
            title = defaultTitle
            subtitle = defaultSubtitle
            iconName = icon
          }

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                border: '1px solid #F0F0F2',
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                padding: '12px',
                ...sx,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: 38,
                  height: 38,
                  overflow: 'hidden',
                  borderRadius: '12px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(136.18deg, rgba(247, 226, 254, 0.76) 0.50%, #F7E2FE 100%)',
                    pointerEvents: 'none',
                  }}
                />
                <EmtIcon
                  className={iconName}
                  sx={{
                    color: (theme: any) => (theme.palette as any).purple.dark,
                    fontSize: '19px',
                    zIndex: 1,
                    position: 'absolute',
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <EmtTypography
                  variant="body1"
                  color="#1D1929"
                  fontWeight="700"
                  sx={{
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {title}
                </EmtTypography>
                <EmtTypography
                  variant="body2"
                  color="#77757F"
                  sx={{
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {subtitle}
                </EmtTypography>
              </Box>
            </Box>
          )
        })}
      </Box>
    )
  }

  // Default behavior: Summary view with tooltip if multiple dates
  const firstDate = transformedDates[0]
  let title, subtitle
  const defaultTitle =
    dates.length > 1
      ? `À partir du ${formatDate(firstDate.startDate, 'dd/MM/yy')}`
      : firstDate.startDate === firstDate.endDate
      ? `Le ${formatDate(firstDate.startDate, 'dd/MM/yy')}`
      : `${formatDate(firstDate.startDate, 'dd/MM/yy')} au ${formatDate(firstDate.endDate, 'dd/MM/yy')}`

  let defaultSubtitle = ''
  if (dates.length > 1) {
    defaultSubtitle = 'Plusieurs dates'
  } else {
    const { startTime, productSlug, endTime } = transformedDates[0]
    if (productSlug === 'petsitter_home_sitting' || productSlug === 'customer_home_sitting') {
      defaultSubtitle = `Arrivée ${startTime} Départ ${endTime}`
    } else if (productSlug === 'visit_hour' || productSlug === 'walk_hour') {
      defaultSubtitle = `${startTime} (60 minutes)`
    } else if (productSlug === 'visit_half_hour' || productSlug === 'walk_half_hour') {
      defaultSubtitle = `${startTime} (30 minutes)`
    }
  }

  let iconName
  if (variant === 'showService') {
    title = TITLES_MAP[firstDate.productSlug]?.title || firstDate.productSlug
    subtitle = defaultTitle
    iconName = TITLES_MAP[firstDate.productSlug]?.icon || 'emt-appointments'
  } else {
    title = defaultTitle
    subtitle = defaultSubtitle
    iconName = icon
  }

  const stopPropagationHandler = (e) => {
    e.stopPropagation()
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        border: '1px solid',
        borderColor,
        borderRadius: '16px',
        padding: '10px',
        minWidth: '0',
        width: '100%',
        justifyContent: 'space-between',
        maxHeight: '100%',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
        <Box
          sx={{
            position: 'relative',
            width: 38,
            height: 38,
            overflow: 'hidden',
            borderRadius: '12px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexShrink: '0',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: '#FBF1FE',
              pointerEvents: 'none',
            }}
          />
          <EmtIcon
            className={iconName}
            sx={{
              color: (theme: any) => (theme.palette as any).purple.main,
              zIndex: 1,
            }}
          />
        </Box>
        <Box sx={{ maxWidth: '100%', minWidth: '0' }}>
          <EmtTypography
            variant="body2"
            color="#1D1929"
            fontWeight="700"
            sx={{
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {title}
          </EmtTypography>
          <Box sx={{ display: 'flex', gap: '5px', position: 'relative', alignItems: 'center' }}>
            <EmtTypography
              variant="body2"
              color="#77757F"
              sx={{
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                fontSize: '11px',
              }}
            >
              {subtitle}
            </EmtTypography>
            {dates.length > 1 && variant !== 'showService' && (
              <>
                <EmtTypography variant="body2" color="#E8E8EA">
                  |
                </EmtTypography>
                <Box
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    position: 'relative',
                    flexShrink: 0,
                  }}
                  onMouseOver={() => setDetailsOpen(true)}
                  onMouseLeave={() => setDetailsOpen(false)}
                >
                  <EmtTypography
                    variant="body2"
                    color="#AE59C6"
                    sx={{
                      cursor: 'pointer',
                      display: 'inline-block',
                      textDecoration: 'underline',
                      flexShrink: 0,
                      fontSize: '11px',
                    }}
                  >
                    {tc('seeAll')}
                  </EmtTypography>
                  <EmtIcon sx={{ color: '#AE59C6', flexShrink: 0 }} className="emt-info-circle-1" />
                  <EmtPaper
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      display: detailsOpen ? 'block' : 'none',
                      zIndex: 1,
                    }}
                  >
                    <Box
                      onWheelCapture={stopPropagationHandler}
                      onTouchMoveCapture={stopPropagationHandler}
                      sx={{
                        overscrollBehavior: 'contain',
                        padding: '12px',
                        minWidth: '200px',
                        overflowY: 'auto',
                        maxHeight: '200px',
                        gap: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'column',
                      }}
                    >
                      {transformedDates.map((date, index) => {
                        let timeText = date.startTime
                        if (
                          date.productSlug === 'petsitter_home_sitting' ||
                          date.productSlug === 'customer_home_sitting'
                        ) {
                          timeText = `à partir de ${date.startTime}`
                        } else if (date.productSlug === 'visit_hour' || date.productSlug === 'walk_hour') {
                          timeText = `${date.startTime} (60 minutes)`
                        } else if (date.productSlug === 'visit_half_hour' || date.productSlug === 'walk_half_hour') {
                          timeText = `${date.startTime} (30 minutes)`
                        }

                        let tooltipTitle, tooltipSubtitle
                        tooltipTitle =
                          date.startDate === date.endDate
                            ? `Le ${formatDate(date.startDate, 'dd/MM/yy')}`
                            : `Du ${formatDate(date.startDate, 'dd/MM/yy')} au ${formatDate(date.endDate, 'dd/MM/yy')}`
                        tooltipSubtitle = timeText

                        return (
                          <Box
                            key={index}
                            sx={{
                              border: '1px solid #F0F0F2',
                              borderRadius: '16px',
                              padding: '15px',
                              minWidth: '0',
                              width: '100%',
                            }}
                          >
                            <EmtTypography variant="body2" color="#1D1929" fontWeight="700" sx={{ textWrap: 'nowrap' }}>
                              {tooltipTitle}
                            </EmtTypography>
                            <EmtTypography variant="body2" color="#77757F" sx={{ textWrap: 'nowrap' }}>
                              {tooltipSubtitle}
                            </EmtTypography>
                          </Box>
                        )
                      })}
                    </Box>
                  </EmtPaper>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>
      {actionText && (
        <EmtTypography
          variant="body2"
          color="#AE59C6"
          sx={{
            cursor: 'pointer',
            display: 'inline-block',
            textDecoration: 'underline',
          }}
          onClick={actionText}
        >
          Modifier
        </EmtTypography>
      )}
    </Box>
  )
}
